/**
 * Settings → Devices → "This phone": the app lock switch (mobile M4). Shown
 * only inside the Android app — the lock is a device setting, and a browser
 * has nothing to lock with.
 */

import * as SwitchPrimitive from "@radix-ui/react-switch";
import { Fingerprint } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import { appLockAvailable, appLockOn, disableAppLock, enableAppLock, RELOCK_AFTER_MS } from "@/lib/appLock";
import { isNative } from "@/platform";

export function PhoneLock() {
  const [on, setOn] = React.useState(appLockOn);
  const [available, setAvailable] = React.useState<boolean | null>(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    void appLockAvailable().then(setAvailable);
  }, []);

  if (!isNative()) return null;

  const toggle = async (next: boolean) => {
    if (!next) {
      disableAppLock();
      setOn(false);
      return;
    }
    setBusy(true);
    const enabled = await enableAppLock();
    setBusy(false);
    setOn(enabled);
    if (!enabled) toast.error("The lock stays off — the check didn't complete.");
  };

  const minutes = Math.round(RELOCK_AFTER_MS / 60_000);
  return (
    <div className="flex items-start gap-3 border-t border-hairline px-5 py-4">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-hairline bg-raised text-muted">
        <Fingerprint className="h-4 w-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <label htmlFor="app-lock" className="text-sm text-ink">
          Lock this phone's MyDeck
        </label>
        <p className="mt-0.5 text-xs text-muted">
          {available === false
            ? "This phone has no fingerprint, face or screen lock set up, so MyDeck can't lock. Set one up in Android's settings."
            : `Fingerprint, face or the phone's PIN when MyDeck opens, and after ${minutes} minutes away. MyDeck is also hidden in the recent-apps view.`}
        </p>
      </div>
      <SwitchPrimitive.Root
        id="app-lock"
        checked={on}
        disabled={busy || available === false}
        onCheckedChange={(v) => void toggle(v)}
        className="relative mt-1 h-7 w-12 shrink-0 rounded-full border border-hairline bg-raised transition-colors data-[state=checked]:border-brass/50 data-[state=checked]:bg-brass/30 disabled:opacity-50"
      >
        <SwitchPrimitive.Thumb className="block h-5 w-5 translate-x-0.5 rounded-full bg-ink shadow transition-transform data-[state=checked]:translate-x-[1.375rem]" />
      </SwitchPrimitive.Root>
    </div>
  );
}
